<template>
  <div class="flex-1 overflow-auto p-4">
    <!-- Loading skeleton -->
    <div v-if="uiFlags.isFetching" class="flex flex-col gap-3">
      <div
        v-for="i in 6"
        :key="`skeleton-${i}`"
        class="flex items-center justify-between gap-3 bg-white rounded-[8px] border border-[#E5E7EB] p-3 animate-pulse"
      >
        <div class="flex flex-col gap-3 flex-1">
          <div class="h-3.5 w-40 rounded bg-gray-100" />
          <div class="h-3 w-72 rounded bg-gray-100" />
        </div>
        <div class="h-3 w-24 rounded bg-gray-100" />
        <div class="flex gap-1">
          <div class="w-[48px] h-[48px] rounded-[4px] bg-gray-100" />
          <div class="w-[48px] h-[48px] rounded-[4px] bg-gray-100" />
        </div>
      </div>
      <div
        class="flex items-center justify-center gap-2 py-3 text-sm text-gray-500"
      >
        <woot-spinner size="small" />
        {{ $t('TEAMS_SETTINGS.LIST.LOADING') }}
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!teamsList.length"
      class="flex h-full flex-col items-center justify-center text-center px-4"
    >
      <span class="material-symbols-outlined text-[40px] text-gray-300 mb-3">
        groups
      </span>
      <h3 class="text-lg font-bold text-gray-800 mb-2">
        {{ $t('TEAMS_SETTINGS.LIST.EMPTY.TITLE') }}
      </h3>
      <p class="text-sm text-gray-500 max-w-[380px] mb-4">
        {{ $t('TEAMS_SETTINGS.LIST.EMPTY.MESSAGE') }}
      </p>
      <button
        v-if="hasCreatePermission('Teams')"
        type="button"
        class="button-filled px-5 py-3 text-sm rounded-[5px] flex items-center gap-1"
        @click="openCreate"
      >
        <fluent-icon icon="add" />
        {{ $t('TEAMS_SETTINGS.NEW_TEAM') }}
      </button>
    </div>

    <!-- Team cards -->
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="item in paginatedTeams"
        :key="item.id"
        class="flex items-center justify-between gap-3 bg-white rounded-[8px] border border-[#E5E7EB] p-3"
      >
        <!-- Name + description (Figma "Row Tiles": vertical, 12px gap) -->
        <div class="flex flex-col gap-3 flex-1 min-w-0">
          <span class="text-base font-bold text-gray-800 truncate">
            {{ item.name }}
          </span>
          <p class="m-0 text-sm text-gray-500 truncate">
            {{ item.description }}
          </p>
        </div>

        <!-- Member count -->
        <div
          class="flex items-center gap-2 text-sm text-gray-600 flex-1 min-w-0"
        >
          <span class="material-symbols-outlined text-[18px] text-gray-500">
            groups
          </span>
          <span>
            {{
              $t('TEAMS_SETTINGS.LIST.MEMBERS', { count: item.members_count })
            }}
          </span>
        </div>

        <!-- Actions -->
        <div class="flex flex-row items-center gap-1 shrink-0">
          <button
            v-if="hasEditPermission('Teams')"
            v-tooltip.top="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
            class="button-ghost flex items-center justify-center w-[48px] h-[48px] rounded-[4px] cursor-pointer"
            style="border: 1px solid #e5e7eb"
            @click="openEdit(item)"
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.5 15.5H2.7615L12.998 5.2635L11.7365 4.002L1.5 14.2385V15.5ZM0 17V13.6155L13.1905 0.43075C13.3417 0.293416 13.5086 0.187333 13.6913 0.1125C13.8741 0.0375 14.0658 0 14.2663 0C14.4668 0 14.6609 0.0355838 14.8488 0.10675C15.0367 0.177917 15.2032 0.291083 15.348 0.446249L16.5693 1.68275C16.7244 1.82758 16.835 1.99425 16.901 2.18275C16.967 2.37125 17 2.55975 17 2.74825C17 2.94942 16.9657 3.14133 16.897 3.324C16.8283 3.50683 16.7191 3.67383 16.5693 3.825L3.3845 17H0ZM12.3562 4.64375L11.7365 4.002L12.998 5.2635L12.3562 4.64375Z"
                fill="#6B7280"
              />
            </svg>
          </button>
          <button
            v-if="hasDeletePermission('Teams')"
            v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
            class="button-ghost flex items-center justify-center w-[48px] h-[48px] rounded-[4px] cursor-pointer"
            style="border: 1px solid #e5e7eb"
            @click="openDelete(item)"
          >
            <svg
              width="15"
              height="17"
              viewBox="0 0 15 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.80775 16.8845C2.30908 16.8845 1.88308 16.7079 1.52975 16.3547C1.17658 16.0014 1 15.5754 1 15.0768V2.3845H0V0.8845H4.5V0H10.5V0.8845H15V2.3845H14V15.0768C14 15.5819 13.825 16.0095 13.475 16.3595C13.125 16.7095 12.6974 16.8845 12.1923 16.8845H2.80775ZM12.5 2.3845H2.5V15.0768C2.5 15.1666 2.52883 15.2403 2.5865 15.298C2.64417 15.3557 2.71792 15.3845 2.80775 15.3845H12.1923C12.2692 15.3845 12.3398 15.3524 12.4038 15.2883C12.4679 15.2243 12.5 15.1538 12.5 15.0768V2.3845ZM4.904 13.3845H6.40375V4.3845H4.904V13.3845ZM8.59625 13.3845H10.096V4.3845H8.59625V13.3845Z"
                fill="#EF4444"
              />
            </svg>
          </button>
        </div>
      </div>

      <teams-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :total-count="totalCount"
        @page-change="onPageChange"
      />
    </div>
    <team-modal v-if="showCreateModal" :on-close="closeCreate" />
    <team-modal
      v-if="showEditModal"
      :team="editingTeam"
      :on-close="closeEdit"
    />
    <team-delete-modal
      v-if="showDeletePopup"
      :team="selectedTeam"
      :on-close="closeDelete"
      @confirm="confirmDeletion"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import accountMixin from '../../../../mixins/account';
import alertMixin from 'shared/mixins/alertMixin';
import TeamsPagination from './TeamsPagination.vue';
import TeamModal from './TeamModal.vue';
import TeamDeleteModal from './TeamDeleteModal.vue';

export default {
  components: {
    TeamsPagination,
    TeamModal,
    TeamDeleteModal,
  },
  mixins: [hasPermissionMixin, accountMixin, alertMixin],
  data() {
    return {
      showDeletePopup: false,
      selectedTeam: {},
      showEditModal: false,
      editingTeam: {},
      showCreateModal: false,
      currentPage: 1,
      pageSize: 15,
    };
  },
  computed: {
    ...mapGetters({
      teamsList: 'teams/getTeams',
      uiFlags: 'teams/getUIFlags',
    }),
    totalCount() {
      return this.teamsList.length;
    },
    paginatedTeams() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.teamsList.slice(start, start + this.pageSize);
    },
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page;
    },
    openEdit(team) {
      this.editingTeam = team;
      this.showEditModal = true;
    },
    closeEdit() {
      this.showEditModal = false;
      this.editingTeam = {};
    },
    openCreate() {
      this.showCreateModal = true;
    },
    closeCreate() {
      this.showCreateModal = false;
    },
    clampCurrentPage() {
      const lastPage = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
      if (this.currentPage > lastPage) {
        this.currentPage = lastPage;
      }
    },
    async deleteTeam({ id }) {
      try {
        await this.$store.dispatch('teams/delete', id);
        this.showAlert(this.$t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
        this.clampCurrentPage();
      } catch (error) {
        this.showAlert(this.$t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
      }
    },

    confirmDeletion() {
      this.deleteTeam(this.selectedTeam);
      this.closeDelete();
    },
    openDelete(team) {
      this.showDeletePopup = true;
      this.selectedTeam = team;
    },
    closeDelete() {
      this.showDeletePopup = false;
      this.selectedTeam = {};
    },
  },
};
</script>
